import { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomTextInput from '../../components/Forms/CustomTextInput';
import ItemsDataGrid from '../../components/Inventory/ItemsDataGrid';
import QuickEditPopover from '../../components/Inventory/QuickEditPopover';
import StatusFilter from '../../components/Inventory/StatusFilter';
import {
  selectCurrentMenu,
  selectProductsByCategory,
  selectCurrentBusiness,
} from '../../redux/selectors';
import {
  createCategory,
  updateCategory,
  getProductsByCategory,
  getCategoryById,
  addInventoryStock,
  removeInventoryStock,
  adjustInventoryStock,
  updateProduct,
  deleteProduct,
} from '../../redux/actions/inventory/inventory';
import { getMenusV2 } from '../../redux/actions/Menu/menuV2';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import { filterInventoryItems } from '../../utils/inventorySearch';
import PromotionTab from '../PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';

const validationSchema = yup.object({
  name: yup.string().required('Category name is required'),
  description: yup.string(),
});

const CategoryEdit = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const currentMenu = useSelector(selectCurrentMenu);
  const currentBusiness = useSelector(selectCurrentBusiness);

  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([
    'Out of Stock',
    'Low Stock',
    'In Stock',
  ]); // All statuses selected by default = show all items
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Reset pagination when filter changes
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPaginationModel({ ...paginationModel, page: 0 });
  };
  const [categoryItems, setCategoryItems] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryUuid, setCategoryUuid] = useState(null);
  const [originalCategoryName, setOriginalCategoryName] = useState('');
  const isEdit = Boolean(id);
  const promotionsEnabled = usePromotionsEnabled();

  const productsByCategoryArray = useSelector(selectProductsByCategory);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  // Watch the name field to check if it has changed
  const currentName = methods.watch('name');

  // Fetch category data when editing
  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      dispatch(getCategoryById(id))
        .then((response) => {
          const categoryData = response?.data?.data;
          if (categoryData) {
            const name = categoryData.name || '';
            setCategoryName(name);
            setCategoryUuid(categoryData.uuid || null);
            setOriginalCategoryName(name);
            methods.reset({
              name: name,
              description: categoryData.description || '',
            });
          }
        })
        .catch((error) => {
          console.error('Error loading category:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, isEdit, id, methods]);

  // Fetch products when editing a category
  useEffect(() => {
    if (isEdit && id) {
      setLoadingProducts(true);
      dispatch(getProductsByCategory(id)).finally(() =>
        setLoadingProducts(false)
      );
    }
  }, [dispatch, isEdit, id]);

  // Set category items from productsByCategory and populate category field
  useEffect(() => {
    if (isEdit && productsByCategoryArray && categoryName) {
      // Populate category field for each product with current category data
      const itemsWithCategory = productsByCategoryArray.map((item) => ({
        ...item,
        category: {
          _id: id,
          name: categoryName,
        },
      }));
      setCategoryItems(itemsWithCategory);
    } else if (isEdit && productsByCategoryArray) {
      // If categoryName not loaded yet, just set items without category
      setCategoryItems(productsByCategoryArray);
    }
  }, [isEdit, productsByCategoryArray, categoryName, id]);

  // Update selectedItem when categoryItems change (after stock actions)
  const selectedItemId = selectedItem?._id;
  useEffect(() => {
    if (selectedItemId && categoryItems.length > 0) {
      const updatedItem = categoryItems.find(
        (item) => item._id === selectedItemId
      );
      if (updatedItem) {
        setSelectedItem(updatedItem);
      }
    }
  }, [categoryItems, selectedItemId]);

  // Filter items based on search and status
  const filteredItems = useMemo(() => {
    return filterInventoryItems(categoryItems, search, statusFilter);
  }, [search, statusFilter, categoryItems]);

  // useEffect(() => {
  //   if (isEdit) {
  //     // TODO: Load category data for editing
  //     // For now, set some mock data
  //     methods.reset({
  //       name: 'Pizza',
  //       description: 'Pizza category',
  //     });
  //     // Set mock items for this category
  //     setCategoryItems([
  //       {
  //         id: '1',
  //         name: 'Margarita',
  //         unit: 'piece',
  //         unitPrice: 12.99,
  //         stock: 23,
  //       },
  //       {
  //         id: '2',
  //         name: 'Pepperoni',
  //         unit: 'piece',
  //         unitPrice: 14.99,
  //         stock: 15,
  //       },
  //     ]);
  //   }
  // }, [isEdit, methods]);

  const handleSave = async (data) => {
    setLoading(true);
    try {
      const formData = {
        name: data.name,
        description: data.description || '',
        menuId: currentMenu?._id || '6597a4c6323faf000ef241fe', // Get menu ID from Redux menu state
      };

      if (isEdit) {
        await dispatch(updateCategory(id, formData));
      } else {
        await dispatch(createCategory(formData));
      }

      // Refresh menu data after successful save
      await dispatch(getMenusV2(false));

      history.push('/inventory/categories');
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push('/inventory/categories');
  };

  const handleEditItem = (item) => {
    const sp = new URLSearchParams();
    sp.set('name', item.name);
    history.push({
      pathname: `/inventory/items/${item._id}`,
      search: sp.toString(),
    });
  };

  const handleDeleteItem = async (item) => {
    const result = await confirm({
      title: t('Confirm Item Deletion'),
      message: t('Are you sure you want to delete this item?'),
      confirmText: t('confirm_modal.delete'),
      cancelText: t('confirm_modal.cancel'),
      background: '#E21508',
    });

    if (result === true) {
      try {
        setLoading(true);
        await dispatch(deleteProduct(item._id));
        // Refresh menu data after successful delete
        await dispatch(getMenusV2(false));
        // Refresh the list to update the table
        await refreshProducts();
        // Close popover if the deleted item was selected
        if (selectedItem?._id === item._id) {
          handleClosePopover();
        }
      } catch (error) {
        console.error('Error deleting item:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleQuickView = (event, row) => {
    setSelectedItem(row);
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = () => {
    setSelectedItem(null);
    setPopoverAnchor(null);
  };

  const refreshProducts = async () => {
    if (isEdit && id) {
      setLoadingProducts(true);
      const response = await dispatch(getProductsByCategory(id));
      setLoadingProducts(false);
      return response;
    }
    return null;
  };

  const handleSaveItem = async (updatedItem) => {
    try {
      setLoading(true);
      // Prepare update data with price and unitPrice at top level
      const updateData = {
        price: updatedItem.price,
      };

      // Add unitPrice at top level if present
      if (updatedItem.unitPrice !== undefined) {
        updateData.unitPrice = updatedItem.unitPrice;
      } else if (updatedItem.inventoryInfo?.unitPrice !== undefined) {
        // Fallback for backward compatibility
        updateData.unitPrice = updatedItem.inventoryInfo.unitPrice;
      }

      // Use updateProduct for price and unitPrice updates
      const response = await dispatch(
        updateProduct(updatedItem._id, updateData)
      );

      // Refresh menu data after successful save
      await dispatch(getMenusV2(false));

      // Update selectedItem with the new data immediately
      if (response?.data?.data && selectedItem?._id === updatedItem._id) {
        setSelectedItem(response.data.data);
      }

      // Refresh the inventory list to update the table
      await refreshProducts();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStockAction = async (action, item, quantity) => {
    try {
      setLoading(true);
      const inventory = item.inventoryInfo || {};

      // Only include uuid, stock, and businessUuid fields
      const requestData = {
        uuid: inventory.uuid || item.uuid || '',
        stock: Number(quantity),
        businessUuid: currentBusiness?.uuid || '',
      };

      let apiCall;
      switch (action) {
        case 'add':
          apiCall = addInventoryStock(requestData);
          break;
        case 'remove':
          apiCall = removeInventoryStock(requestData);
          break;
        case 'adjust':
          apiCall = adjustInventoryStock(requestData);
          break;
        default:
          throw new Error('Invalid action');
      }

      await dispatch(apiCall);

      // Refresh menu data after successful stock action
      await dispatch(getMenusV2(false));

      // Refresh the inventory list to get updated stock values
      // The useEffect hooks will automatically update categoryItems and selectedItem
      await refreshProducts();
    } catch (error) {
      console.error('Error updating stock:', error);
    } finally {
      setLoading(false);
    }
  };

  console.log('currentName', currentName);
  console.log('originalCategoryName', originalCategoryName);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 1,
          px: 3,
          py: '20px',
        }}
      >
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSave)}>
            {/* Title */}
            <Typography
              sx={{
                mb: '20px',
                fontWeight: '400',
                fontSize: '22px',
              }}
            >
              {isEdit ? t('Edit Category') : t('Add Category')}
            </Typography>
            <Divider sx={{ mx: -3, mb: 4 }} />

            {/* Form Fields */}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              <CustomTextInput
                name="name"
                label={`${t('Category Name')} *`}
                disabled={loading}
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                sx={{ minWidth: 100 }}
              >
                {t('CANCEL')}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={
                  loading || (isEdit && currentName === originalCategoryName)
                }
                sx={{ minWidth: 100 }}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {t('SAVE')}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </Box>
      {isEdit && (
        <Box
          sx={{ backgroundColor: 'white', borderRadius: 2, boxShadow: 1, p: 2 }}
        >
          {/* Category Items Table */}
          <Box>
            {/* Search and Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                gap: 2,
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}
              >
                <TextField
                  size="small"
                  placeholder={t('Search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <StatusFilter
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  disabled={loading || loadingProducts}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => history.push('/inventory/items/new')}
                  startIcon={<AddIcon />}
                  disabled={loading}
                  sx={{ py: '7px' }}
                >
                  {t('ADD NEW')}
                </Button>
              </Box>
            </Box>
            <ItemsDataGrid
              rows={filteredItems}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={loadingProducts}
              onEdit={handleEditItem}
              onDelete={handleDeleteItem}
              onQuickView={handleQuickView}
            />
          </Box>
        </Box>
      )}

      {isEdit && promotionsEnabled && (
        <Box
          sx={{ backgroundColor: 'white', borderRadius: 2, boxShadow: 1, p: 2 }}
        >
          <Typography sx={{ fontWeight: '400', fontSize: '18px', mb: 2 }}>
            {t('Promotions')}
          </Typography>
          <PromotionTab
            lockedCategoryIds={[categoryUuid]}
            lockedCategoryName={categoryName}
          />
        </Box>
      )}

      {/* Quick Edit Popover */}
      <QuickEditPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        item={selectedItem}
        onSave={handleSaveItem}
        onStockAction={handleStockAction}
        loading={loading}
      />
    </Box>
  );
};

export default CategoryEdit;
